import {
  rafCallback,
} from '../../utils/raf.ts';

/**
 * Hover detection for tooltip targets the pointer passes straight through.
 *
 * The theme gives disabled components `pointer-events: none`, so they never receive
 * `mouseenter`, and their tooltip, usually the one explaining why they are disabled, would
 * never show. The document still sees every move, so one shared listener follows the pointer
 * and looks for an unreachable target beneath it. Targets the pointer can reach keep relying
 * on their own `mouseenter` and `mouseleave`.
 */

export interface IQTooltipHoverSubscriber {
  target: HTMLElement,
  onHoverStart: () => void,
  onHoverEnd: () => void,
}

export interface IQTooltipPointerPosition {
  x: number,
  y: number,
}

const subscribers = new Set<IQTooltipHoverSubscriber>();

let hoveredSubscriber: IQTooltipHoverSubscriber | null = null;

let lastPointerPosition: IQTooltipPointerPosition | null = null;

function isPointInside(element: HTMLElement, point: IQTooltipPointerPosition): boolean {
  const elementRect = element.getBoundingClientRect();
  return point.x >= elementRect.left
    && point.x < elementRect.right
    && point.y >= elementRect.top
    && point.y < elementRect.bottom;
}

/**
 * Whether the pointer at `point`, currently hitting `hitElement`, is over `target`: either it
 * reaches the target, or it passes through the target to one of its ancestors. An ancestor hit
 * also proves nothing else covers the target there, so a dialog over a disabled button does
 * not count as hovering the button.
 */
export function isPointerOverTarget(target: HTMLElement, hitElement: Element | null, point: IQTooltipPointerPosition): boolean {
  if (!hitElement) {
    return false;
  }
  if (target.contains(hitElement)) {
    return true;
  }
  return hitElement.contains(target)
    && isPointInside(target, point)
    && getComputedStyle(target).pointerEvents === 'none';
}

function findUnreachableSubscriber(hitElement: Element, point: IQTooltipPointerPosition): IQTooltipHoverSubscriber | null {
  let innermostSubscriber: IQTooltipHoverSubscriber | null = null;

  for (const subscriber of subscribers) {
    const isReachable = subscriber.target.contains(hitElement);
    if (!isReachable && isPointerOverTarget(subscriber.target, hitElement, point)) {
      const isInnermost = !innermostSubscriber || innermostSubscriber.target.contains(subscriber.target);
      if (isInnermost) {
        innermostSubscriber = subscriber;
      }
    }
  }
  return innermostSubscriber;
}

function updateHoveredSubscriber(): void {
  const point = lastPointerPosition;
  if (!point) {
    return;
  }
  const hitElement = document.elementFromPoint(point.x, point.y);

  // A target that has become reachable again hears its own `mouseleave` from here on.
  if (hoveredSubscriber && hitElement && hoveredSubscriber.target.contains(hitElement)) {
    hoveredSubscriber = null;
  }

  const nextSubscriber = hitElement ? findUnreachableSubscriber(hitElement, point) : null;
  if (nextSubscriber === hoveredSubscriber) {
    return;
  }
  hoveredSubscriber?.onHoverEnd();
  hoveredSubscriber = nextSubscriber;
  nextSubscriber?.onHoverStart();
}

const scheduleUpdate = rafCallback(updateHoveredSubscriber);

function onDocumentMouseMove(event: MouseEvent): void {
  lastPointerPosition = {
    x: event.clientX,
    y: event.clientY,
  };
  scheduleUpdate();
}

function onDocumentMouseOut(event: MouseEvent): void {
  const hasLeftDocument = !event.relatedTarget;
  if (!hasLeftDocument) {
    return;
  }
  lastPointerPosition = null;
  hoveredSubscriber?.onHoverEnd();
  hoveredSubscriber = null;
}

function bindDocumentListeners(): void {
  document.addEventListener('mousemove', onDocumentMouseMove, {
    capture: true,
    passive: true,
  });
  document.addEventListener('mouseout', onDocumentMouseOut, {
    capture: true,
    passive: true,
  });
  document.addEventListener('scroll', scheduleUpdate, {
    capture: true,
    passive: true,
  });
}

function unbindDocumentListeners(): void {
  document.removeEventListener('mousemove', onDocumentMouseMove, {
    capture: true,
  });
  document.removeEventListener('mouseout', onDocumentMouseOut, {
    capture: true,
  });
  document.removeEventListener('scroll', scheduleUpdate, {
    capture: true,
  });
}

export function trackUnreachableHover(subscriber: IQTooltipHoverSubscriber): void {
  if (subscribers.size === 0) {
    bindDocumentListeners();
  }
  subscribers.add(subscriber);
}

export function untrackUnreachableHover(subscriber: IQTooltipHoverSubscriber): void {
  if (!subscribers.delete(subscriber)) {
    return;
  }
  if (hoveredSubscriber === subscriber) {
    hoveredSubscriber = null;
  }
  if (subscribers.size === 0) {
    unbindDocumentListeners();
    lastPointerPosition = null;
  }
}
